import DropdownTreeSelect from "react-dropdown-tree-select";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Switch, Text } from "@pentabd/ui";

import { SelectController } from "./SelectController";
import { SelectControllerVertical } from "./SelectControllerVertical";

import { useBaseData } from "@hooks/useBaseData";
import { useLanguage } from "@hooks/useLanguage";
import { useMasterData } from "@hooks/useMasterData";

import {
  getConvertedGradeData,
  getConvertedPositionData,
  getConvertedPositionDataWithPostType,
  getConvertedUnitData,
} from "@helpers/baseDataConvert";
import {
  getFilteredGradeData,
  getFilteredUnitPositionData,
} from "@helpers/convertedGradeAndUnitPositionData";
import { getPositionColor } from "@helpers/getPositionColor";
import { unitTreeDataConvert } from "@helpers/unitTreeDataConvert";

import { BASE_DATA } from "@constants/base-data";

import { PositionType } from "@type/HRM/position";
import { EXTRA_RESPONSIBILITY } from "@validations/HRM/employee/extraResponsibilityValidation";
import { useEffect } from "react";

const { WINGS, POSITIONS, UNIT_TREE, POST_TYPE, DESIGNATIONS, GRADES } =
  BASE_DATA;
const { WING_DESK_IO_E0, DESIGNATION, POSITION, GRADE } = EXTRA_RESPONSIBILITY;

export function SelectEmployeeForExtraResponsibility({
  showAllEmployees,
  selectAllPositions,
}: {
  showAllEmployees?: boolean;
  selectAllPositions?: boolean;
}) {
  const { t } = useTranslation();
  const { language } = useLanguage();

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  // Those data are for some dropdowns or radio buttons options.
  const { data: positions } = useBaseData(POSITIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: unitTree } = useBaseData(UNIT_TREE);
  const { data: postType } = useMasterData(POST_TYPE);
  const { data: designationData } = useBaseData(DESIGNATIONS);
  const { data: grades } = useBaseData(GRADES);

  let wing = watch(WING_DESK_IO_E0) as string;
  let designation = watch(DESIGNATION) as string;
  let showAllDesignations = watch("showAllDesignations") as boolean;

  useEffect(() => {
    if (designation && !showAllDesignations) {
      setValue(
        GRADE,
        getFilteredGradeData(
          positions,
          wing,
          designation,
          language,
          showAllEmployees ? "viewOnly" : "filledPosition",
        )[0]?.value,
      );
    } else if (designation && showAllDesignations) {
      setValue(
        GRADE,
        designationData.find((item: any) => item.id === designation)
          ?.gradeDetails?.id,
      );
      setValue(POSITION, null);
    }
  }, [designation, showAllDesignations]);

  return (
    <div className="row mb-0 align-items-center">
      <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
        <Text weight="semibold" size="sm" color="title">
          {t("EMPLOYEES.EXTRA_RESPONSIBILITY.FORM.WING.LABEL")}
          <span className="text-danger">&#42;</span>
        </Text>

        {selectAllPositions && (
          <div className="d-flex align-items-center">
            <Text weight="semibold" size="sm" color="title" className="me-2">
              {t(
                "EMPLOYEES.EXTRA_RESPONSIBILITY.FORM.SHOW_ALL_DESIGNATIONS.LABEL",
              )}
            </Text>
            <Controller
              control={control}
              name="showAllDesignations"
              render={({ field }) => {
                return (
                  <Switch
                    {...register("showAllDesignations")}
                    id="showAllDesignations"
                    name="permissions"
                    size="sm"
                    controlling
                    onChange={(e) => {
                      setValue("showAllDesignations", e.target.checked);
                    }}
                  />
                );
              }}
            />
          </div>
        )}
      </div>

      <div className="col-lg-6">
        <Controller
          control={control}
          name={WING_DESK_IO_E0}
          render={({ field }) => {
            return (
              <DropdownTreeSelect
                data={unitTreeDataConvert(
                  unitTree[0]?.children || [],
                  language,
                )}
                onChange={(e) => setValue(WING_DESK_IO_E0, e.id as number)}
                mode="radioSelect"
                texts={{
                  placeholder: t(
                    "EMPLOYEES.EXTRA_RESPONSIBILITY.FORM.WING.PLACEHOLDER",
                  ),
                }}
                className="w-100"
              />
            );
          }}
        />
      </div>

      <div className="col-lg-6">
        {wing && (
          <div>
            <SelectController
              control={control}
              name={WING_DESK_IO_E0}
              register={{ ...register(WING_DESK_IO_E0) }}
              options={getConvertedUnitData(wings, language)}
              setValue={setValue}
              disabled
            />
          </div>
        )}
      </div>

      {wing && (
        <SelectControllerVertical
          label={"EMPLOYEE.GENERAL_INFO.FORM.DESIGNATION"}
          control={control}
          name={DESIGNATION}
          register={{ ...register(DESIGNATION) }}
          options={
            showAllDesignations
              ? getConvertedPositionData(designationData, language)
              : getConvertedPositionDataWithPostType(
                  positions
                    .filter((position: PositionType) => {
                      return showAllEmployees
                        ? position.unit.id === Number(wing)
                        : position.unit.id === Number(wing) &&
                            !position.employee?.id;
                    })
                    .map((position: PositionType) => ({
                      position: position.position,
                      postType: position.postType,
                    })),
                  language,
                ).map((position: any) => ({
                  ...position,
                  customComponent: (
                    <Text
                      color={getPositionColor(postType as any, position as any)}
                    >
                      {position.label}
                    </Text>
                  ),
                }))
          }
          setValue={setValue}
          isSearchable
          required
          error={errors}
        />
      )}

      {wing && designation && (
        <>
          <SelectControllerVertical
            label="EMPLOYEE.GENERAL_INFO.FORM.GRADE"
            control={control}
            name={GRADE}
            register={{ ...register(GRADE) }}
            options={
              showAllDesignations
                ? getConvertedGradeData(grades, language)
                : getFilteredGradeData(
                    positions,
                    wing,
                    designation,
                    language,
                    showAllEmployees ? "viewOnly" : "filledPosition",
                  )
            }
            setValue={setValue}
            required
            error={errors}
            disabled={showAllDesignations}
          />

          <SelectControllerVertical
            label="EMPLOYEE.GENERAL_INFO.FORM.POSITION"
            control={control}
            name={POSITION}
            register={{ ...register(POSITION) }}
            options={getFilteredUnitPositionData(
              positions,
              wing,
              designation,
              language,
              showAllEmployees ? "viewOnly" : "filledPosition",
            )}
            setValue={setValue}
            required={!showAllDesignations}
            error={errors}
            disabled={showAllDesignations}
          />
        </>
      )}
    </div>
  );
}
